import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, Store, MapPin, Phone, Monitor, Clock, Warehouse, Check, Settings, Percent, FileText, Building, CreditCard, Grid3X3, ShoppingCart, Globe, Upload, X, Loader2, AlertTriangle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { storesAPI, warehousesAPI, transfersAPI, businessSettingsAPI } from '@/services/api';
import api from '@/services/api';
import AddressForm from '@/components/AddressForm';

const initialFormData = {
  code: '',
  name: '',
  gstin: '',
  pan: '',
  fssai_license: '',
  assigned_warehouse_id: '',
  assigned_warehouse_ids: [],
  store_type: 'own',
  franchise_settings: {
    franchise_name: '',
    franchise_owner: '',
    franchise_pan: '',
    franchise_contact: '',
    franchise_email: '',
    franchise_pricing_level_id: '',
    credit_limit: 0,
    credit_limit_enforcement: 'soft',
    credit_warning_threshold: 80,
    payment_terms_days: 30
  },
  address: {
    address_line1: '',
    address_line2: '',
    country: 'India',
    state: '',
    district: '',
    area: '',
    pincode: ''
  },
  contact: {
    phone: '',
    email: '',
    alternate_phone: ''
  },
  counters: [
    { counter_id: 'C01', counter_name: 'Counter 1', is_active: true }
  ],
  shifts: [
    { shift_id: 'S1', name: 'Morning', start_time: '09:00', end_time: '17:00', is_active: true },
    { shift_id: 'S2', name: 'Evening', start_time: '17:00', end_time: '23:00', is_active: true }
  ],
  discount_settings: {
    allow_bill_discount: false,
    max_discount_percent: 0
  },
  bank_details: {
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch: '',
    account_type: 'Current'
  },
  gst_invoice_settings: {
    enabled: false,
    terms_and_conditions: '',
    show_shipping_address: false,
    default_shipping_same_as_billing: true,
    signature_image_url: '',
    signatory_name: '',
    show_bank_details: true,
    footer_note: ''
  },
  einvoice_credentials: {
    provider: 'masters_india',
    api_key: '',
    api_secret: '',
    client_id: '',
    client_secret: '',
    einvoice_username: '',
    einvoice_password: '',
    sandbox_mode: true,
    is_configured: false
  },
  invoice_prefix: '',
  ecommerce_enabled: false,
  photos: [],
  latitude: '',
  longitude: '',
  map_share_url: '',
  branch_email: '',
};

const StoresPage = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [pricingLevels, setPricingLevels] = useState([]);
  const [hqGstin, setHqGstin] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [storageConfigured, setStorageConfigured] = useState(false);
  const signatureFileRef = useRef(null);
  const { toast } = useToast();

  const fetchStores = useCallback(async () => {
    try {
      const response = await storesAPI.list({ search });
      setStores(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch stores',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  const fetchWarehouses = async () => {
    try {
      const response = await warehousesAPI.list({ is_active: true });
      setWarehouses(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
    }
  };

  const fetchPricingLevels = async () => {
    try {
      const response = await transfersAPI.listPricingLevels();
      setPricingLevels(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pricing levels:', error);
    }
  };

  const fetchHqGstin = async () => {
    try {
      const response = await businessSettingsAPI.get();
      setHqGstin(response.data?.gstin || '');
    } catch (error) {
      console.error('Failed to fetch HQ GSTIN:', error);
    }
  };

  const checkStorageConfig = async () => {
    try {
      const response = await api.get('/assets/settings');
      setStorageConfigured(response.data?.is_configured || false);
    } catch {
      setStorageConfigured(false);
    }
  };

  const handleSignatureUpload = async (file) => {
    if (!storageConfigured) {
      toast({ 
        title: 'Error', 
        description: 'Asset storage not configured. Please configure Bunny CDN in HQ Settings → Asset Storage.', 
        variant: 'destructive' 
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Error', description: 'Only image files are allowed', variant: 'destructive' });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'Error', description: 'File too large. Max 2MB allowed', variant: 'destructive' });
      return;
    }

    setUploadingSignature(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('entity_type', 'signatures');
      uploadFormData.append('entity_id', selectedStore?.id || 'new');
      
      const response = await api.post('/assets/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          gst_invoice_settings: { 
            ...prev.gst_invoice_settings, 
            signature_image_url: response.data.cdn_url 
          }
        }));
        toast({ title: 'Success', description: 'Signature image uploaded' });
      }
    } catch (error) {
      toast({ 
        title: 'Upload Failed', 
        description: error.response?.data?.detail || 'Failed to upload signature', 
        variant: 'destructive' 
      });
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleDeleteSignature = () => {
    setFormData(prev => ({
      ...prev,
      gst_invoice_settings: { 
        ...prev.gst_invoice_settings, 
        signature_image_url: '' 
      }
    }));
  };

  useEffect(() => {
    fetchStores();
    fetchWarehouses();
    fetchPricingLevels();
    fetchHqGstin();
    checkStorageConfig();
  }, [fetchStores]);

  const handleOpenDialog = (store = null) => {
    if (store) {
      setSelectedStore(store);
      setFormData({
        code: store.code,
        name: store.name,
        gstin: store.gstin || '',
        pan: store.pan || '',
        fssai_license: store.fssai_license || '',
        assigned_warehouse_id: store.assigned_warehouse_id || '',
        assigned_warehouse_ids: store.assigned_warehouse_ids || [store.assigned_warehouse_id].filter(Boolean),
        store_type: store.store_type || 'own',
        franchise_settings: store.franchise_settings || initialFormData.franchise_settings,
        address: {
          address_line1: store.address?.address_line1 || '',
          address_line2: store.address?.address_line2 || '',
          country: store.address?.country || 'India',
          state: store.address?.state || '',
          district: store.address?.district || store.address?.city || '',
          area: store.address?.area || '',
          pincode: store.address?.pincode || ''
        },
        contact: store.contact || initialFormData.contact,
        counters: store.counters || initialFormData.counters,
        shifts: store.shifts || initialFormData.shifts,
        discount_settings: store.discount_settings || initialFormData.discount_settings,
        bank_details: store.bank_details || initialFormData.bank_details,
        gst_invoice_settings: store.gst_invoice_settings || initialFormData.gst_invoice_settings,
        ecommerce_enabled: store.ecommerce_enabled || false,
        photos: store.photos || [],
        latitude: store.latitude ?? '',
        longitude: store.longitude ?? '',
        map_share_url: store.map_share_url || '',
        branch_email: store.branch_email || '',
      });
    } else {
      setSelectedStore(null);
      setFormData(initialFormData);
    }
    setActiveTab('basic');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name || !formData.gstin || formData.assigned_warehouse_ids.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Code, Name, GSTIN, and at least one Warehouse are required',
        variant: 'destructive'
      });
      return;
    }

    // GST Validation for Own Stores
    const storeType = formData.store_type === 'own' ? 'owned' : formData.store_type;
    if (storeType === 'owned' && hqGstin && formData.gstin !== hqGstin) {
      toast({
        title: 'GST Validation Error',
        description: `Own stores must use the same GSTIN as HQ (${hqGstin}). Use "Franchise" store type for different GSTIN.`,
        variant: 'destructive'
      });
      return;
    }

    if (!formData.address.state || !formData.address.district || !formData.address.pincode) {
      toast({
        title: 'Validation Error',
        description: 'State, District, and Pincode are required',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      // Validate discount settings
      if (formData.discount_settings.allow_bill_discount) {
        const maxPercent = parseFloat(formData.discount_settings.max_discount_percent);
        if (isNaN(maxPercent) || maxPercent < 0 || maxPercent > 100) {
          toast({
            title: 'Validation Error',
            description: 'Maximum discount percentage must be between 0 and 100',
            variant: 'destructive'
          });
          setSaving(false);
          return;
        }
      }

      // Primary warehouse is the first selected
      const payload = {
        ...formData,
        assigned_warehouse_id: formData.assigned_warehouse_ids[0],
        assigned_warehouse_ids: formData.assigned_warehouse_ids,
        latitude: formData.latitude === '' ? null : parseFloat(formData.latitude),
        longitude: formData.longitude === '' ? null : parseFloat(formData.longitude),
        address: {
          ...formData.address,
          city: formData.address.district // Map district to city for backward compatibility
        },
        discount_settings: {
          allow_bill_discount: formData.discount_settings.allow_bill_discount,
          max_discount_percent: parseFloat(formData.discount_settings.max_discount_percent) || 0
        }
      };

      if (selectedStore) {
        await storesAPI.update(selectedStore.id, payload);
        toast({ title: 'Success', description: 'Store updated successfully' });
      } else {
        await storesAPI.create(payload);
        toast({ title: 'Success', description: 'Store created successfully' });
      }
      setDialogOpen(false);
      fetchStores();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save store',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await storesAPI.delete(selectedStore.id);
      toast({ title: 'Success', description: 'Store deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchStores();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete store',
        variant: 'destructive'
      });
    }
  };

  const handleAddressChange = (newAddress) => {
    setFormData(prev => ({ ...prev, address: newAddress }));
  };

  const updateContactField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const toggleWarehouse = (warehouseId) => {
    setFormData(prev => {
      const currentIds = prev.assigned_warehouse_ids || [];
      if (currentIds.includes(warehouseId)) {
        return {
          ...prev,
          assigned_warehouse_ids: currentIds.filter(id => id !== warehouseId)
        };
      } else {
        return {
          ...prev,
          assigned_warehouse_ids: [...currentIds, warehouseId]
        };
      }
    });
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    const parts = [];
    if (addr.area) parts.push(addr.area);
    if (addr.district || addr.city) parts.push(addr.district || addr.city);
    if (addr.state) parts.push(addr.state);
    if (addr.pincode) parts.push(addr.pincode);
    return parts.join(', ');
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Stores</h1>
          <p className="text-gray-500">Manage your SY-Store retail locations</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-200"
          data-testid="add-store-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Store
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search stores..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-300 text-gray-800 focus:border-emerald-500"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Stores Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : stores.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <Store className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No stores found</p>
            <Button
              onClick={() => handleOpenDialog()}
              variant="link"
              className="text-emerald-600 mt-2"
            >
              Add your first store
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {stores.map(store => (
            <Card
              key={store.id}
              className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`store-card-${store.code}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-gray-800 text-lg">{store.name}</CardTitle>
                    <p className="text-sm text-emerald-600 font-medium mt-1">{store.code}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {store.store_type === 'franchise' && (
                      <Badge className="bg-orange-100 text-orange-700">
                        <Users className="w-3 h-3 mr-1" /> Franchise
                      </Badge>
                    )}
                    {store.ecommerce_enabled && (
                      <Badge className="bg-indigo-100 text-indigo-700">
                        <ShoppingCart className="w-3 h-3 mr-1" /> E-Com
                      </Badge>
                    )}
                    <Badge className={store.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                      {store.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-gray-500">GSTIN: {store.gstin}</div>
                
                {store.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{formatAddress(store.address)}</span>
                  </div>
                )}
                
                <div className="flex items-start gap-2 text-sm">
                  <Warehouse className="w-4 h-4 text-blue-500 mt-0.5" />
                  <div className="flex flex-wrap gap-1">
                    {(store.assigned_warehouse_ids || [store.assigned_warehouse_id]).filter(Boolean).map(whId => {
                      const wh = warehouses.find(w => w.id === whId);
                      return wh ? (
                        <Badge key={whId} variant="outline" className="text-xs border-blue-200 text-blue-600">
                          {wh.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Monitor className="w-4 h-4 text-purple-500" />
                    <span className="text-gray-600">{store.counters?.length || 0} Counters</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="text-gray-600">{store.shifts?.length || 0} Shifts</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-50"
                    onClick={() => handleOpenDialog(store)}
                    data-testid={`edit-store-${store.code}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                    onClick={() => navigate(`/hq/stores/${store.id}/rack-settings`)}
                    data-testid={`rack-settings-${store.code}`}
                    title="Rack & Revenue Settings"
                  >
                    <Grid3X3 className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-500 hover:bg-red-50"
                    onClick={() => {
                      setSelectedStore(store);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-store-${store.code}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">
              {selectedStore ? 'Edit Store' : 'Add Store'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-gray-100 w-full justify-start flex-wrap">
              <TabsTrigger value="basic" className="data-[state=active]:bg-white">Basic Info</TabsTrigger>
              <TabsTrigger value="address" className="data-[state=active]:bg-white">Address</TabsTrigger>
              <TabsTrigger value="warehouses" className="data-[state=active]:bg-white">Warehouses</TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-white">Settings</TabsTrigger>
              <TabsTrigger value="gst-invoice" className="data-[state=active]:bg-white">GST Invoice</TabsTrigger>
              <TabsTrigger value="einvoice" className="data-[state=active]:bg-white">
                <FileText className="w-4 h-4 mr-1" /> E-Invoice
              </TabsTrigger>
              <TabsTrigger value="ecommerce" className="data-[state=active]:bg-white">
                <ShoppingCart className="w-4 h-4 mr-1" /> E-Commerce
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Store Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., SY001"
                    className="bg-gray-50 border-gray-300"
                    disabled={!!selectedStore}
                    data-testid="store-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Store Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Store Name"
                    className="bg-gray-50 border-gray-300"
                    data-testid="store-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">GSTIN *</Label>
                  <Input
                    value={formData.gstin}
                    onChange={(e) => setFormData(p => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                    placeholder="22AAAAA0000A1Z5"
                    className={`bg-gray-50 border-gray-300 ${
                      (formData.store_type === 'owned' || formData.store_type === 'own') && 
                      hqGstin && formData.gstin && formData.gstin !== hqGstin 
                        ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                    data-testid="store-gstin-input"
                  />
                  {(formData.store_type === 'owned' || formData.store_type === 'own') && hqGstin && (
                    <p className={`text-xs ${formData.gstin === hqGstin ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formData.gstin === hqGstin 
                        ? '✓ Matches HQ GSTIN' 
                        : `⚠ Own stores must use HQ GSTIN: ${hqGstin}`}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">PAN</Label>
                  <Input
                    value={formData.pan}
                    onChange={(e) => setFormData(p => ({ ...p, pan: e.target.value.toUpperCase() }))}
                    placeholder="AAAAA0000A"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">FSSAI License</Label>
                <Input
                  value={formData.fssai_license}
                  onChange={(e) => setFormData(p => ({ ...p, fssai_license: e.target.value }))}
                  placeholder="FSSAI Number"
                  className="bg-gray-50 border-gray-300"
                />
              </div>

              {/* Store Type */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-blue-200">
                  <Building className="w-5 h-5 text-blue-600" />
                  <h3 className="font-medium text-blue-800">Store Type</h3>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-700">Ownership Type *</Label>
                  <Select
                    value={formData.store_type}
                    onValueChange={(v) => setFormData(p => ({ ...p, store_type: v }))}
                  >
                    <SelectTrigger className="bg-white border-gray-300" data-testid="store-type-select">
                      <SelectValue placeholder="Select store type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="own">
                        <div className="flex items-center gap-2">
                          <Store className="w-4 h-4 text-emerald-500" />
                          <span>Own Store</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="franchise">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-orange-500" />
                          <span>Franchise Store</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {formData.store_type === 'own' 
                      ? 'Company-owned store with standard pricing' 
                      : 'Franchise store with special pricing and payment terms'}
                  </p>
                </div>

                {/* Franchise Settings - Only show when store_type is franchise */}
                {formData.store_type === 'franchise' && (
                  <div className="space-y-4 pt-4 border-t border-blue-200">
                    <h4 className="text-sm font-medium text-orange-700 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Franchise Details
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Franchise Name</Label>
                        <Input
                          value={formData.franchise_settings?.franchise_name || ''}
                          onChange={(e) => setFormData(p => ({ 
                            ...p, 
                            franchise_settings: { ...p.franchise_settings, franchise_name: e.target.value }
                          }))}
                          placeholder="Business/Company name"
                          className="bg-white border-gray-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Franchise Owner</Label>
                        <Input
                          value={formData.franchise_settings?.franchise_owner || ''}
                          onChange={(e) => setFormData(p => ({ 
                            ...p, 
                            franchise_settings: { ...p.franchise_settings, franchise_owner: e.target.value }
                          }))}
                          placeholder="Owner name"
                          className="bg-white border-gray-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Franchise PAN</Label>
                        <Input
                          value={formData.franchise_settings?.franchise_pan || ''}
                          onChange={(e) => setFormData(p => ({ 
                            ...p, 
                            franchise_settings: { ...p.franchise_settings, franchise_pan: e.target.value.toUpperCase() }
                          }))}
                          placeholder="AAAAA0000A"
                          className="bg-white border-gray-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Contact Number</Label>
                        <Input
                          value={formData.franchise_settings?.franchise_contact || ''}
                          onChange={(e) => setFormData(p => ({ 
                            ...p, 
                            franchise_settings: { ...p.franchise_settings, franchise_contact: e.target.value }
                          }))}
                          placeholder="Contact phone"
                          className="bg-white border-gray-300"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-700">Franchise Email</Label>
                      <Input
                        value={formData.franchise_settings?.franchise_email || ''}
                        onChange={(e) => setFormData(p => ({ 
                          ...p, 
                          franchise_settings: { ...p.franchise_settings, franchise_email: e.target.value }
                        }))}
                        placeholder="email@franchise.com"
                        className="bg-white border-gray-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-700">Pricing Level *</Label>
                      <Select
                        value={formData.franchise_settings?.franchise_pricing_level_id || 'none'}
                        onValueChange={(v) => setFormData(p => ({ 
                          ...p, 
                          franchise_settings: { ...p.franchise_settings, franchise_pricing_level_id: v === 'none' ? '' : v }
                        }))}
                      >
                        <SelectTrigger className="bg-white border-gray-300" data-testid="pricing-level-select">
                          <SelectValue placeholder="Select pricing level" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {pricingLevels.length === 0 ? (
                            <SelectItem value="none" disabled>
                              No pricing levels found. Create one in Ledgers → Franchise Pricing
                            </SelectItem>
                          ) : (
                            <>
                              <SelectItem value="none">
                                <span className="text-gray-500">Select a pricing level...</span>
                              </SelectItem>
                              {pricingLevels.map(level => (
                                <SelectItem key={level.id} value={level.id}>
                                  <div className="flex items-center gap-2">
                                    <span>{level.name}</span>
                                    <span className="text-xs text-gray-500">
                                      ({level.pricing_method === 'cost_plus' 
                                        ? `Cost +${level.margin_percent}%` 
                                        : `MRP -${level.discount_percent}%`})
                                    </span>
                                    {level.is_default && (
                                      <Badge variant="outline" className="text-xs">Default</Badge>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        This determines the transfer price when stock is sent to this franchise
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Credit Limit (₹)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="1000"
                          value={formData.franchise_settings?.credit_limit || 0}
                          onChange={(e) => setFormData(p => ({ 
                            ...p, 
                            franchise_settings: { ...p.franchise_settings, credit_limit: parseFloat(e.target.value) || 0 }
                          }))}
                          placeholder="0"
                          className="bg-white border-gray-300"
                          data-testid="franchise-credit-limit"
                        />
                        <p className="text-xs text-gray-500">Maximum outstanding amount allowed</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Credit Enforcement</Label>
                        <Select
                          value={formData.franchise_settings?.credit_limit_enforcement || 'soft'}
                          onValueChange={(v) => setFormData(p => ({ 
                            ...p, 
                            franchise_settings: { ...p.franchise_settings, credit_limit_enforcement: v }
                          }))}
                        >
                          <SelectTrigger className="bg-white border-gray-300" data-testid="credit-enforcement-select">
                            <SelectValue placeholder="Select enforcement type" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200">
                            <SelectItem value="none">
                              <span className="text-gray-600">None - No limit enforcement</span>
                            </SelectItem>
                            <SelectItem value="soft">
                              <span className="text-amber-600">Soft - Warning only</span>
                            </SelectItem>
                            <SelectItem value="hard">
                              <span className="text-red-600">Hard - Block orders</span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          {formData.franchise_settings?.credit_limit_enforcement === 'hard' 
                            ? 'Orders will be blocked if credit limit exceeded' 
                            : formData.franchise_settings?.credit_limit_enforcement === 'soft'
                            ? 'Warning will be shown but order can proceed'
                            : 'Credit limit will not be enforced'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Payment Terms (Days)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="365"
                          value={formData.franchise_settings?.payment_terms_days || 30}
                          onChange={(e) => setFormData(p => ({ 
                            ...p, 
                            franchise_settings: { ...p.franchise_settings, payment_terms_days: parseInt(e.target.value) || 30 }
                          }))}
                          placeholder="30"
                          className="bg-white border-gray-300"
                        />
                        <p className="text-xs text-gray-500">Days to clear dues after transfer</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">Warning Threshold (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.franchise_settings?.credit_warning_threshold || 80}
                          onChange={(e) => setFormData(p => ({ 
                            ...p, 
                            franchise_settings: { ...p.franchise_settings, credit_warning_threshold: parseFloat(e.target.value) || 80 }
                          }))}
                          placeholder="80"
                          className="bg-white border-gray-300"
                          data-testid="credit-warning-threshold"
                        />
                        <p className="text-xs text-gray-500">Show warning when balance reaches this % of limit</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Phone</Label>
                  <Input
                    value={formData.contact.phone}
                    onChange={(e) => updateContactField('phone', e.target.value)}
                    placeholder="Phone number"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Email</Label>
                  <Input
                    value={formData.contact.email}
                    onChange={(e) => updateContactField('email', e.target.value)}
                    placeholder="Email address"
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="address" className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                <h3 className="text-sm font-medium text-emerald-800 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Store Address
                </h3>
                <AddressForm
                  address={formData.address}
                  onChange={handleAddressChange}
                />
              </div>
            </TabsContent>

            <TabsContent value="warehouses" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-2">
                  <Warehouse className="w-4 h-4" />
                  Linked Warehouses *
                </Label>
                <p className="text-xs text-gray-500 mb-2">Select all warehouses that supply to this store</p>
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg space-y-2 max-h-64 overflow-y-auto">
                  {warehouses.length === 0 ? (
                    <p className="text-sm text-gray-500">No warehouses available. Create a warehouse first.</p>
                  ) : (
                    warehouses.map(warehouse => (
                      <div
                        key={warehouse.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          formData.assigned_warehouse_ids.includes(warehouse.id)
                            ? 'bg-blue-100 border-blue-300'
                            : 'bg-white border-gray-200 hover:border-blue-200'
                        }`}
                        onClick={() => toggleWarehouse(warehouse.id)}
                      >
                        <Checkbox
                          checked={formData.assigned_warehouse_ids.includes(warehouse.id)}
                          onCheckedChange={() => toggleWarehouse(warehouse.id)}
                          className="pointer-events-none"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{warehouse.name}</p>
                          <p className="text-xs text-gray-500">
                            {warehouse.code} • {warehouse.address?.district || warehouse.address?.city || 'No location'}
                          </p>
                        </div>
                        {formData.assigned_warehouse_ids.includes(warehouse.id) && (
                          <Check className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>
                {formData.assigned_warehouse_ids.length > 0 && (
                  <p className="text-xs text-blue-600">
                    {formData.assigned_warehouse_ids.length} warehouse(s) selected
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="settings" className="space-y-4 mt-4">
              {/* Store Settings */}
              <div className="space-y-6">
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-3 mb-4">
                    <Percent className="w-5 h-5 text-blue-500" />
                    <h3 className="font-medium text-gray-800">Bill Discount Settings</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-gray-700">Allow Bill Discount</Label>
                        <p className="text-xs text-gray-500 mt-1">
                          Enable to allow cashiers and managers to give bill-level discounts
                        </p>
                      </div>
                      <Switch
                        checked={formData.discount_settings?.allow_bill_discount || false}
                        onCheckedChange={(checked) => 
                          setFormData(p => ({
                            ...p,
                            discount_settings: {
                              ...p.discount_settings,
                              allow_bill_discount: checked
                            }
                          }))
                        }
                        data-testid="allow-discount-switch"
                      />
                    </div>

                    {formData.discount_settings?.allow_bill_discount && (
                      <div className="space-y-2 pt-3 border-t border-gray-200">
                        <Label className="text-gray-700">Maximum Discount Percentage *</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={formData.discount_settings?.max_discount_percent || ''}
                            onChange={(e) => 
                              setFormData(p => ({
                                ...p,
                                discount_settings: {
                                  ...p.discount_settings,
                                  max_discount_percent: e.target.value
                                }
                              }))
                            }
                            placeholder="e.g., 10"
                            className="bg-white border-gray-300 w-32"
                            data-testid="max-discount-input"
                          />
                          <span className="text-gray-500">%</span>
                        </div>
                        <p className="text-xs text-amber-600">
                          Store users can only give discounts up to this percentage. Set 0 to disable discounts.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 border border-gray-200 rounded-lg bg-blue-50">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> When bill discount is disabled, the discount option will be hidden from the POS screen for this store.
                    When enabled, strict validation will ensure discounts do not exceed the configured maximum percentage.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* GST Invoice Settings Tab */}
            <TabsContent value="gst-invoice" className="space-y-6 mt-4">
              {/* Bank Details Section */}
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3 mb-4">
                  <Building className="w-5 h-5 text-blue-500" />
                  <h3 className="font-medium text-gray-800">Bank Details for GST Invoice</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Bank Name</Label>
                      <Input
                        value={formData.bank_details?.bank_name || ''}
                        onChange={(e) => setFormData(p => ({
                          ...p,
                          bank_details: { ...p.bank_details, bank_name: e.target.value }
                        }))}
                        placeholder="e.g., State Bank of India"
                        className="bg-white border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Account Number</Label>
                      <Input
                        value={formData.bank_details?.account_number || ''}
                        onChange={(e) => setFormData(p => ({
                          ...p,
                          bank_details: { ...p.bank_details, account_number: e.target.value }
                        }))}
                        placeholder="e.g., 1234567890"
                        className="bg-white border-gray-300"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">IFSC Code</Label>
                      <Input
                        value={formData.bank_details?.ifsc_code || ''}
                        onChange={(e) => setFormData(p => ({
                          ...p,
                          bank_details: { ...p.bank_details, ifsc_code: e.target.value.toUpperCase() }
                        }))}
                        placeholder="e.g., SBIN0001234"
                        className="bg-white border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Branch</Label>
                      <Input
                        value={formData.bank_details?.branch || ''}
                        onChange={(e) => setFormData(p => ({
                          ...p,
                          bank_details: { ...p.bank_details, branch: e.target.value }
                        }))}
                        placeholder="e.g., Chennai Main"
                        className="bg-white border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Account Type</Label>
                      <select
                        value={formData.bank_details?.account_type || 'Current'}
                        onChange={(e) => setFormData(p => ({
                          ...p,
                          bank_details: { ...p.bank_details, account_type: e.target.value }
                        }))}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md bg-white"
                      >
                        <option value="Current">Current</option>
                        <option value="Savings">Savings</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* GST Invoice Settings Section */}
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-green-500" />
                  <h3 className="font-medium text-gray-800">A4 GST Invoice Settings</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-gray-700">Enable GST Invoice</Label>
                      <p className="text-xs text-gray-500 mt-1">
                        Allow generating A4 format GST invoices from POS
                      </p>
                    </div>
                    <Switch
                      checked={formData.gst_invoice_settings?.enabled || false}
                      onCheckedChange={(checked) => setFormData(p => ({
                        ...p,
                        gst_invoice_settings: { ...p.gst_invoice_settings, enabled: checked }
                      }))}
                    />
                  </div>

                  {formData.gst_invoice_settings?.enabled && (
                    <>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                        <div className="space-y-2">
                          <Label className="text-gray-700">Signatory Name</Label>
                          <Input
                            value={formData.gst_invoice_settings?.signatory_name || ''}
                            onChange={(e) => setFormData(p => ({
                              ...p,
                              gst_invoice_settings: { ...p.gst_invoice_settings, signatory_name: e.target.value }
                            }))}
                            placeholder="e.g., John Doe (Manager)"
                            className="bg-white border-gray-300"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-700">Signature Image</Label>
                          <div className="flex items-start gap-4">
                            {formData.gst_invoice_settings?.signature_image_url ? (
                              <div className="relative group">
                                <img 
                                  src={formData.gst_invoice_settings.signature_image_url} 
                                  alt="Signature" 
                                  className="w-32 h-16 object-contain rounded border border-gray-200 bg-white p-1"
                                />
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="absolute -top-2 -right-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={handleDeleteSignature}
                                  type="button"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <div 
                                className={`w-32 h-16 border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer transition-colors ${storageConfigured ? 'border-gray-300 hover:border-teal-500 hover:bg-teal-50' : 'border-gray-200 bg-gray-50 cursor-not-allowed'}`}
                                onClick={() => storageConfigured && signatureFileRef.current?.click()}
                              >
                                {uploadingSignature ? (
                                  <Loader2 className="w-4 h-4 text-teal-500 animate-spin" />
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 text-gray-400" />
                                    <span className="text-xs text-gray-500 mt-1">Upload</span>
                                  </>
                                )}
                              </div>
                            )}
                            <input 
                              ref={signatureFileRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleSignatureUpload(file);
                                e.target.value = '';
                              }}
                            />
                            <div className="flex-1 text-xs text-gray-500">
                              <p>Authorized signature for invoices</p>
                              {!storageConfigured && (
                                <p className="text-amber-600 mt-1 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  Configure Asset Storage first
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-gray-700">Show Bank Details on Invoice</Label>
                          <p className="text-xs text-gray-500 mt-1">Display bank information for payment</p>
                        </div>
                        <Switch
                          checked={formData.gst_invoice_settings?.show_bank_details !== false}
                          onCheckedChange={(checked) => setFormData(p => ({
                            ...p,
                            gst_invoice_settings: { ...p.gst_invoice_settings, show_bank_details: checked }
                          }))}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-gray-700">Show Shipping Address</Label>
                          <p className="text-xs text-gray-500 mt-1">Separate Ship To section on invoice</p>
                        </div>
                        <Switch
                          checked={formData.gst_invoice_settings?.show_shipping_address || false}
                          onCheckedChange={(checked) => setFormData(p => ({
                            ...p,
                            gst_invoice_settings: { ...p.gst_invoice_settings, show_shipping_address: checked }
                          }))}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-700">Terms & Conditions</Label>
                        <Textarea
                          value={formData.gst_invoice_settings?.terms_and_conditions || ''}
                          onChange={(e) => setFormData(p => ({
                            ...p,
                            gst_invoice_settings: { ...p.gst_invoice_settings, terms_and_conditions: e.target.value }
                          }))}
                          placeholder="Enter terms and conditions for GST invoice..."
                          className="bg-white border-gray-300 min-h-[100px]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-700">Footer Note</Label>
                        <Input
                          value={formData.gst_invoice_settings?.footer_note || ''}
                          onChange={(e) => setFormData(p => ({
                            ...p,
                            gst_invoice_settings: { ...p.gst_invoice_settings, footer_note: e.target.value }
                          }))}
                          placeholder="e.g., Thank you for your business!"
                          className="bg-white border-gray-300"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="p-4 border border-gray-200 rounded-lg bg-blue-50">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> GST Invoice settings are used when generating A4 format tax invoices. 
                  Bank details will be printed on the invoice for payment reference.
                  Configure these settings for each store as needed.
                </p>
              </div>
            </TabsContent>

            {/* E-Invoice & E-Way Bill Tab */}
            <TabsContent value="einvoice" className="space-y-6 mt-4">
              <div className="p-6 border border-gray-200 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-emerald-100 rounded-lg">
                      <FileText className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">E-Invoice & E-Way Bill Settings</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Configure Masters India credentials for this store's GSTIN
                      </p>
                      <Badge variant="outline" className="mt-2 text-emerald-700 border-emerald-300">
                        GSTIN: {formData.gstin || 'Not set'}
                      </Badge>
                    </div>
                  </div>
                  <Switch
                    checked={formData.einvoice_credentials?.is_configured || false}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      einvoice_credentials: {
                        ...prev.einvoice_credentials,
                        is_configured: checked
                      }
                    }))}
                    className="data-[state=checked]:bg-emerald-600"
                  />
                </div>

                {/* Provider Selection */}
                <div className="bg-white p-4 rounded-lg border border-emerald-200 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Provider</Label>
                      <Select
                        value={formData.einvoice_credentials?.provider || 'masters_india'}
                        onValueChange={(val) => setFormData(prev => ({
                          ...prev,
                          einvoice_credentials: {
                            ...prev.einvoice_credentials,
                            provider: val
                          }
                        }))}
                      >
                        <SelectTrigger className="bg-gray-50 border-gray-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="masters_india">Masters India</SelectItem>
                          <SelectItem value="cleartax">ClearTax</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Invoice Prefix</Label>
                      <Input
                        value={formData.invoice_prefix || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_prefix: e.target.value }))}
                        placeholder={`e.g., ${formData.code || 'SY001'}-INV`}
                        className="bg-gray-50 border-gray-300"
                      />
                      <p className="text-xs text-gray-500">Used for invoice numbering: PREFIX-2025-0001</p>
                    </div>
                  </div>
                </div>

                {/* Sandbox Mode */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Switch
                    checked={formData.einvoice_credentials?.sandbox_mode ?? true}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      einvoice_credentials: {
                        ...prev.einvoice_credentials,
                        sandbox_mode: checked
                      }
                    }))}
                    className="data-[state=checked]:bg-amber-500"
                  />
                  <div>
                    <span className="font-medium text-amber-800">Sandbox Mode</span>
                    <p className="text-xs text-amber-600">Enable for testing. Disable for production.</p>
                  </div>
                </div>

                {/* API Credentials */}
                <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
                  <h4 className="font-medium text-gray-800 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    API Credentials
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">API Key</Label>
                      <Input
                        type="password"
                        value={formData.einvoice_credentials?.api_key || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          einvoice_credentials: {
                            ...prev.einvoice_credentials,
                            api_key: e.target.value
                          }
                        }))}
                        placeholder="Enter API Key"
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">API Secret</Label>
                      <Input
                        type="password"
                        value={formData.einvoice_credentials?.api_secret || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          einvoice_credentials: {
                            ...prev.einvoice_credentials,
                            api_secret: e.target.value
                          }
                        }))}
                        placeholder="Enter API Secret"
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Client ID</Label>
                      <Input
                        value={formData.einvoice_credentials?.client_id || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          einvoice_credentials: {
                            ...prev.einvoice_credentials,
                            client_id: e.target.value
                          }
                        }))}
                        placeholder="Enter Client ID"
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Client Secret</Label>
                      <Input
                        type="password"
                        value={formData.einvoice_credentials?.client_secret || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          einvoice_credentials: {
                            ...prev.einvoice_credentials,
                            client_secret: e.target.value
                          }
                        }))}
                        placeholder="Enter Client Secret"
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h5 className="font-medium text-gray-700 mb-3">E-Invoice Portal Credentials</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700">E-Invoice Username</Label>
                        <Input
                          value={formData.einvoice_credentials?.einvoice_username || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            einvoice_credentials: {
                              ...prev.einvoice_credentials,
                              einvoice_username: e.target.value
                            }
                          }))}
                          placeholder="GST Portal Username"
                          className="bg-gray-50 border-gray-300"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700">E-Invoice Password</Label>
                        <Input
                          type="password"
                          value={formData.einvoice_credentials?.einvoice_password || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            einvoice_credentials: {
                              ...prev.einvoice_credentials,
                              einvoice_password: e.target.value
                            }
                          }))}
                          placeholder="GST Portal Password"
                          className="bg-gray-50 border-gray-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 border border-emerald-200 rounded-lg bg-emerald-50">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div className="text-sm text-emerald-800">
                    <strong>How to get Masters India credentials:</strong>
                    <ol className="list-decimal list-inside mt-2 space-y-1 text-emerald-700">
                      <li>Sign up at <a href="https://www.mastersindia.co" target="_blank" rel="noopener noreferrer" className="underline">mastersindia.co</a></li>
                      <li>Complete KYC with your GSTIN</li>
                      <li>Get API credentials from the dashboard</li>
                      <li>Use sandbox mode for testing first</li>
                    </ol>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* E-Commerce Tab */}
            <TabsContent value="ecommerce" className="space-y-6 mt-4">
              <div className="p-6 border border-gray-200 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-indigo-100 rounded-lg">
                      <ShoppingCart className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">E-Commerce Facility</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Enable this store to serve online orders from the E-Commerce website
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.ecommerce_enabled}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, ecommerce_enabled: checked }))}
                    className="data-[state=checked]:bg-indigo-600"
                  />
                </div>
                
                {formData.ecommerce_enabled && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-indigo-200">
                    <div className="flex items-center gap-2 text-indigo-700">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">E-Commerce Enabled</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      This store can now be mapped to delivery zones. Go to <strong>E-Commerce → Delivery Zones</strong> to map districts to this store.
                    </p>
                  </div>
                )}
                
                {!formData.ecommerce_enabled && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-500">
                      When enabled, this store will be available for online order fulfillment based on delivery zone mappings.
                    </p>
                  </div>
                )}
              </div>

              <div className="p-4 border border-amber-200 rounded-lg bg-amber-50">
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Delivery Zone Mapping Required</p>
                    <p className="text-sm text-amber-700 mt-1">
                      After enabling E-Commerce, map districts to this store in the <strong>Delivery Zones</strong> settings. 
                      Customers will see products from this store based on their delivery pincode.
                    </p>
                  </div>
                </div>
              </div>

              {/* Branch listing (public "Our Stores" page) */}
              <div className="p-4 border rounded-lg bg-white" data-testid="branch-listing-block">
                <div className="mb-3">
                  <p className="text-sm font-medium">Public "Our Stores" listing</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Shown on the storefront's Our Stores page. Add up to 2 photos, coordinates for the map pin, and a public branch email.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                      placeholder="e.g. 13.0389"
                      data-testid="branch-lat"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                      placeholder="e.g. 80.2619"
                      data-testid="branch-lng"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Google Maps share URL (optional)</Label>
                    <Input
                      value={formData.map_share_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, map_share_url: e.target.value }))}
                      placeholder="Auto-generated from lat/lng when blank"
                      data-testid="branch-map-url"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Public branch email (optional)</Label>
                    <Input
                      type="email"
                      value={formData.branch_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, branch_email: e.target.value }))}
                      placeholder="saidapet@soonyoung.in"
                      data-testid="branch-email"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label className="text-xs mb-2 block">Branch photos (up to 2)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[0, 1].map((slot) => {
                      const url = formData.photos?.[slot];
                      return (
                        <div key={slot} className="relative border-2 border-dashed rounded-md aspect-video overflow-hidden bg-slate-50">
                          {url ? (
                            <>
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!selectedStore) {
                                    setFormData(prev => ({ ...prev, photos: (prev.photos || []).filter((_, i) => i !== slot) }));
                                    return;
                                  }
                                  try {
                                    await storesAPI.deleteBranchPhoto?.(selectedStore.id, url);
                                    setFormData(prev => ({ ...prev, photos: (prev.photos || []).filter((_, i) => i !== slot) }));
                                    toast({ title: 'Photo removed' });
                                  } catch (e) {
                                    toast({ title: 'Delete failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
                                  }
                                }}
                                className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white text-xs px-2 py-0.5 rounded"
                                data-testid={`branch-photo-remove-${slot}`}
                              >
                                Remove
                              </button>
                            </>
                          ) : (
                            <label className="w-full h-full flex flex-col items-center justify-center text-xs text-gray-500 cursor-pointer hover:bg-slate-100">
                              <span className="text-2xl mb-1">+</span>
                              <span>Upload photo {slot + 1}</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                data-testid={`branch-photo-upload-${slot}`}
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file || !selectedStore) {
                                    if (!selectedStore) {
                                      toast({ title: 'Save the store first', description: 'Photos can be added after the store record exists.', variant: 'destructive' });
                                    }
                                    return;
                                  }
                                  const fd = new FormData();
                                  fd.append('file', file);
                                  try {
                                    const res = await storesAPI.uploadBranchPhoto(selectedStore.id, fd);
                                    setFormData(prev => ({ ...prev, photos: res.data.photos }));
                                    toast({ title: 'Photo uploaded' });
                                  } catch (err) {
                                    toast({ title: 'Upload failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
                                  }
                                }}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {!selectedStore && (
                    <p className="text-xs text-amber-700 mt-2">💡 Save the store first, then re-open it to upload photos.</p>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-gray-300 text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="save-store-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate Store?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate the store &quot;{selectedStore?.name}&quot;. This action can be reversed later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StoresPage;
